import { useState, useCallback } from "react";
import { AiOutlinePlus } from "react-icons/ai";
import { SkewLoader } from "react-spinners";
import useEditFormStore from "../../../../../../state-management/useEditFormStore";
import useCompanyStore from "../../../../../../state-management/useCompanyStore";
import { useGetCustomTaxRelief } from "../../../../../../services/custom-tax-relief.service";
import {
	CustomTaxReliefDto,
	CustomTaxReliefOrderBy,
} from "../../../../../../models/custom-tax-relief.dto";
import { APP_CONFIG } from "../../../../../../helpers/appHelpers";
import AssignedTaxReliefRow from "./AssignedTaxReliefRow";
import AddTaxReliefForm from "./AddTaxReliefForm";

const EmployeeCustomTaxReliefs = () => {
	const { currentCompany } = useCompanyStore();
	const { formDataValues } = useEditFormStore();
	const [showAddForm, setShowAddForm] = useState(false);

	const { data, isLoading, refetch } = useGetCustomTaxRelief({
		companyId: currentCompany?.id,
		limit: APP_CONFIG.PAGE_LIMIT,
		orderBy: CustomTaxReliefOrderBy.NAME_ASC,
	});

	const allReliefs: CustomTaxReliefDto[] = data?.data || [];

	const assignedReliefs = allReliefs.filter((relief) =>
		relief.employeeCustomTaxReliefs?.some(
			(ectr) => ectr.employeeId === formDataValues?.id,
		),
	);

	const unassignedReliefs = allReliefs.filter(
		(relief) =>
			relief.active &&
			!relief.employeeCustomTaxReliefs?.some(
				(ectr) => ectr.employeeId === formDataValues?.id,
			),
	);

	const handleChange = useCallback(() => refetch(), [refetch]);

	return (
		<div className="mb-4">
			<div className="border-b-[1.5px] pb-3 mb-4 flex w-full justify-between text-sm">
				<h2 className="font-semibold">Custom Tax Reliefs</h2>
				{!showAddForm && (
					<button
						onClick={() => setShowAddForm(true)}
						className="border-none flex items-center text-primary-blue"
					>
						<AiOutlinePlus className="mr-2 text-lg" />
						Assign Tax Relief
					</button>
				)}
			</div>

			{showAddForm && (
				<AddTaxReliefForm
					employeeId={formDataValues?.id}
					availableReliefs={unassignedReliefs}
					setShowAddForm={setShowAddForm}
					onSuccess={handleChange}
				/>
			)}

			{isLoading && (
				<div className="text-center mt-10 py-10">
					<SkewLoader color="#F58B00" />
				</div>
			)}

			{!isLoading && assignedReliefs.length > 0 && (
				<div className="mt-2 space-y-2">
					{assignedReliefs.map((relief) => (
						<AssignedTaxReliefRow
							key={relief.id}
							employeeId={formDataValues?.id}
							relief={relief}
							onRemoved={handleChange}
						/>
					))}
				</div>
			)}

			{!isLoading && assignedReliefs.length === 0 && !showAddForm && (
				<h3 className="text-center text-sm font-semibold mt-10">
					No custom tax reliefs assigned yet. Click 'Assign Tax Relief' to
					begin.
				</h3>
			)}
		</div>
	);
};

export default EmployeeCustomTaxReliefs;
